import {
  ContractGenerationProcessComponentState,
  ContractGenerationProcessDealershipBankAccount,
  ContractGenerationProcessInitialData,
  ContractGenerationProcessMappedResult,
  ContractGenerationProcessPerson,
  ContractGenerationProcessPurchaseOrder,
  ContractGenerationProcessRegime,
  ContractGenerationProcessResponse,
} from "../interfaces/contract-generation-process.interface";

function resolveRegime(titular: ContractGenerationProcessPerson | null): ContractGenerationProcessRegime {
  if (!titular) {
    return "";
  }

  return titular.regime === "pm" ? "moral" : "fisica";
}

function resolveAgencyName(
  application: ContractGenerationProcessResponse["application"],
  purchaseOrder: ContractGenerationProcessPurchaseOrder
): string {
  return (
    application.dealership?.business_name ||
    purchaseOrder.agency_info?.business_name ||
    purchaseOrder.agency_info?.name ||
    ""
  );
}

function resolveAgencyAddress(
  application: ContractGenerationProcessResponse["application"],
  purchaseOrder: ContractGenerationProcessPurchaseOrder
): string {
  return application.dealership?.full_address || purchaseOrder.agency_info?.address || "";
}

function resolveBankAccountsForLegalEntity(
  dealershipsBankAccounts: ContractGenerationProcessDealershipBankAccount[],
  purchaseOrder: ContractGenerationProcessPurchaseOrder
): {
  bankAccounts: ContractGenerationProcessDealershipBankAccount[];
  foundBankAccounts: boolean;
} {
  if (!dealershipsBankAccounts.length) {
    return { bankAccounts: [], foundBankAccounts: false };
  }

  const legalEntityId = purchaseOrder.agency_info?.legal_entity_id;
  const bankAccounts = dealershipsBankAccounts.filter((bankAccount) => bankAccount.holder_id === legalEntityId);

  return {
    bankAccounts,
    foundBankAccounts: bankAccounts.length > 0,
  };
}

function mapInitialData(response: ContractGenerationProcessResponse): ContractGenerationProcessInitialData {
  const { application, purchase_order: purchaseOrder } = response;

  return {
    application,
    contract: response.contract,
    documents: response.documents,
    buro: response.buro,
    xml_info: response.xml_info,
    titular_person: response.titular_person,
    contact_person: response.contact_person,
    car_info: response.car_info ?? purchaseOrder.car_info ?? {},
    purchase_order: purchaseOrder,
    key_collection_section: response.meta.key_collection_section,
    states: response.states,
    invoice_information: application.account?.invoice_information ?? null,
    kyc_profile: application.kyc_profile ?? null,
    representatives: response.representatives ?? [],
    dealerships: response.dealerships_bank_accounts ?? [],
    agency_accounts: response.agency_accounts_document,
    downpayments: response.downpayments ?? [],
    policy_initial_data: response.policy_initial_data ?? null,
    specialist_full_name: application?.internal_user?.full_name ?? null,
    client_bank_accounts: response.client_bank_accounts ?? [],
    contract_bank_accounts: response.contract_bank_accounts ?? null,
    siting_state: response.siting_state ?? null,
    applicants: response.applicants ?? [],
    is_first_time_client: response.meta?.is_first_time_client ?? true,
  };
}

function mapComponentState(response: ContractGenerationProcessResponse): ContractGenerationProcessComponentState {
  const { application, purchase_order: purchaseOrder } = response;
  const { bankAccounts, foundBankAccounts } = resolveBankAccountsForLegalEntity(
    response.dealerships_bank_accounts ?? [],
    purchaseOrder
  );

  return {
    application,
    contract: response.contract,
    kyc_profile: application.kyc_profile ?? null,
    selected_bank_account_id: application.account?.disbursement_bank_account_id ?? null,
    titular: response.titular_person,
    contact_person: response.contact_person,
    regime: resolveRegime(response.titular_person),
    documents: response.documents,
    purchase_order: purchaseOrder,
    dealership_id: response.meta.dealership_id,
    dealerships_response: response.dealerships_bank_accounts ?? [],
    representatives: response.representatives ?? [],
    agency_accounts: response.agency_accounts_document,
    agency_name: resolveAgencyName(application, purchaseOrder),
    agency_address: resolveAgencyAddress(application, purchaseOrder),
    bank_accounts: bankAccounts,
    found_bank_accounts: foundBankAccounts,
    previous_contract_warning: Boolean(response.contract?.approved),
    invoice_information: application.account?.invoice_information ?? null,
    downpayments: response.downpayments ?? [],
    buro: response.buro,
  };
}

export function mapContractGenerationProcess(
  response: ContractGenerationProcessResponse
): ContractGenerationProcessMappedResult {
  return {
    initialData: mapInitialData(response),
    componentState: mapComponentState(response),
    meta: response.meta,
    applicants: response.applicants ?? [],
  };
}
